import type { Inbox, Message, MessageTemplate, MessageThread } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Mail, MessageSquare, Phone, Send, Sparkles, StickyNote } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { callAction } from "../api";
import { useLocale, useT } from "../i18n";
import { formatPhone, shortDate } from "../theme";
import { useToast } from "./Toast";
import { Badge, Button, Input, Select, Textarea, cn } from "./ui";

export type InboxMeta = Pick<Inbox, "sendWindowOpen" | "quietHoursNote" | "smsMode" | "emailMode" | "sms">;

export function invalidateMessaging(qc: ReturnType<typeof useQueryClient>, customerId: string) {
  qc.invalidateQueries({ queryKey: ["inbox"] });
  qc.invalidateQueries({ queryKey: ["thread", customerId] });
  qc.invalidateQueries({ queryKey: ["customer", customerId] });
  qc.invalidateQueries({ queryKey: ["today"] });
}

const isLive = (state?: string) => state === "live" || state === "test";

/** Honest texting facts: state, sender, allowlist, and the exact blocker when texts are not going out. Used in the Inbox header and above every conversation. */
export function SmsFactsBar({ sms, compact }: { sms?: Inbox["sms"]; compact?: boolean }) {
  const t = useT();
  if (!sms) return null;
  const live = isLive(sms.state);
  return (
    <div className={cn("rounded-2xl border px-3 py-2 text-xs", live ? "border-primary/30 bg-primary/5" : "border-ink/10 bg-surface")}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-ink/70">{t("msg.texting")}</span>
        <Badge tone={live ? "green" : sms.state === "demo" ? "neutral" : "amber"}>{t(`msg.state.${sms.state}`)}</Badge>
        <span className="text-ink/60">{t("msg.sender")}: {sms.from ? <span className="font-mono">{formatPhone(sms.from) ?? sms.from}</span> : t("msg.sender.none")}</span>
        {live ? <Badge tone={sms.allowlistOnly ? "blue" : "amber"}>{sms.allowlistOnly ? `${t("msg.allowlist.on")} · ${sms.allowlistCount} ${t("msg.allowlist.count")}` : t("msg.allowlist.off")}</Badge> : null}
      </div>
      {!compact ? <p className="mt-1 text-ink/60">{sms.senderNote}{live ? ` ${t("msg.liveNote")}` : ""}</p> : null}
      {sms.blocker ? <p className="mt-1 text-amber-900"><span className="font-semibold">{t("msg.blocker")}:</span> {sms.blocker}</p> : null}
    </div>
  );
}

/** Conversation + composer. Used full-size in the Inbox and compact on the customer page. Same actions either way. */
export function Conversation({ customerId, meta, compact }: { customerId: string; meta?: InboxMeta; compact?: boolean }) {
  const t = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const { toast } = useToast();

  const thread = useQuery({
    queryKey: ["thread", customerId],
    queryFn: async () => (await callAction<"agency.message.list", MessageThread>("agency.message.list", { customerId, limit: 200, unreadOnly: false })).result,
    refetchInterval: 10_000,
  });
  const metaQ = useQuery({
    queryKey: ["inbox", "meta"],
    queryFn: async () => (await callAction<"agency.message.list", Inbox>("agency.message.list", { limit: 1, unreadOnly: false })).result,
    enabled: !meta,
    staleTime: 30_000,
  });
  const m: InboxMeta | undefined = meta ?? metaQ.data;
  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: async () => (await callAction<"agency.template.list", MessageTemplate[]>("agency.template.list", {})).result,
    staleTime: 300_000,
  });

  const markRead = useMutation({
    mutationFn: () => callAction<"agency.message.mark_read", { updated: number }>("agency.message.mark_read", { customerId }),
    onSuccess: () => invalidateMessaging(qc, customerId),
  });
  useEffect(() => {
    if (thread.data && thread.data.unread > 0 && !markRead.isPending) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.data?.unread, customerId]);

  const [channel, setChannel] = useState<"sms" | "email">("sms");
  const [category, setCategory] = useState<"transactional" | "marketing">("transactional");
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [callOpen, setCallOpen] = useState(false);
  const [callSummary, setCallSummary] = useState("");
  const [simOpen, setSimOpen] = useState(false);
  const [simBody, setSimBody] = useState("");

  const send = useMutation({
    mutationFn: () => callAction<"agency.message.send", Message>("agency.message.send", { customerId, channel, body, subject: channel === "email" ? subject || undefined : undefined, category, templateId: templateId || undefined, aiDrafted: false }),
    onSuccess: (r) => {
      const st = r.result.status;
      const blocked = st.startsWith("blocked") || st === "failed";
      toast({ title: blocked ? t("inbox.blocked") : t("inbox.sent"), body: `${t(`inbox.status.${st}`)} · ${t(`mode.${r.result.executionMode}`)}`, tone: blocked ? "warn" : "success" });
      if (!blocked) {
        setBody("");
        setSubject("");
        setTemplateId("");
      }
      invalidateMessaging(qc, customerId);
    },
    onError: (e) => {
      toast({ title: t("inbox.blocked"), body: (e as Error).message, tone: "error" });
      invalidateMessaging(qc, customerId);
    },
  });
  const logCall = useMutation({
    mutationFn: () => callAction<"agency.message.log_call", Message>("agency.message.log_call", { customerId, direction: "outbound", summary: callSummary }),
    onSuccess: () => {
      toast({ title: t("inbox.logged"), tone: "success" });
      setCallSummary("");
      setCallOpen(false);
      invalidateMessaging(qc, customerId);
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const simulate = useMutation({
    mutationFn: () => callAction<"agency.message.simulate_inbound", { message: Message; reply: string | null }>("agency.message.simulate_inbound", { customerId, body: simBody }),
    onSuccess: (r) => {
      toast({ title: t("msg.simulateReply.done"), body: r.result.reply ? `${t("msg.simulateReply.autoReply")}: ${r.result.reply}` : undefined, tone: "success" });
      setSimBody("");
      invalidateMessaging(qc, customerId);
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  const th = thread.data;
  const prefLocale = (th?.preferredLocale === "es" ? "es" : "en") as "en" | "es";
  const channelTemplates = useMemo(() => (templates.data ?? []).filter((x) => x.channel === channel && x.category === category), [templates.data, channel, category]);

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const tp = channelTemplates.find((x) => x.id === id);
    if (!tp) return;
    setBody(tp.body[prefLocale] ?? tp.body.en ?? "");
    if (tp.subject) setSubject(tp.subject[prefLocale] ?? tp.subject.en ?? "");
  };

  // Why can't we send? One reason at a time, in plain words.
  const blockReason = (() => {
    if (!th) return null;
    if (channel === "sms") {
      if (!th.phone) return t("inbox.noPhone");
      if (category === "marketing" && !th.consent.smsMarketing) return t("inbox.marketingBlocked");
      if (!th.consent.smsTransactional && !th.consent.smsMarketing) return t("inbox.smsBlocked");
      if (m && !m.sendWindowOpen) return `${t("inbox.quietHours")} · ${m.quietHoursNote}`;
    } else {
      if (!th.email) return t("inbox.noEmail");
      if (category === "marketing" && !th.consent.emailMarketing) return t("inbox.marketingBlocked");
    }
    return null;
  })();

  if (thread.isLoading) return <p className="py-6 text-center text-ink/60">{t("common.loading")}</p>;
  if (thread.isError || !th) return <p className="py-6 text-center text-red-700">{(thread.error as Error)?.message ?? t("common.error")}</p>;

  const messages = [...th.messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const smsLive = isLive(m?.sms?.state);
  const canSimulate = !!m && !smsLive && !!th.phone;

  return (
    <div className={cn("flex flex-col", compact ? "max-h-[70vh]" : "h-full min-h-0")}>
      {!meta ? <div className="mb-2"><SmsFactsBar sms={m?.sms} compact /></div> : null}
      <div className="flex items-center justify-between gap-2 px-1 pb-2 text-xs text-ink/60">
        <span>{th.phone ? formatPhone(th.phone) : null}{th.phone && th.email ? " · " : ""}{th.email ?? ""}</span>
        <span className="flex items-center gap-1">
          <span className={cn("h-2 w-2 rounded-full", m?.sendWindowOpen ? "bg-primary" : "bg-amber-500")} />
          {m ? (m.sendWindowOpen ? t("inbox.windowOpen") : t("inbox.quietHours")) : ""}
        </span>
      </div>

      <ol className={cn("flex-1 min-h-0 overflow-y-auto space-y-2 px-1 py-2", compact && "max-h-[36vh]")} aria-live="polite">
        {messages.length === 0 ? <li className="py-6 text-center text-sm text-ink/55">{t("common.empty")}</li> : null}
        {messages.map((msg) => (
          <Bubble key={msg.id} msg={msg} locale={locale} />
        ))}
      </ol>

      <form
        className="border-t border-ink/10 pt-3 space-y-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (body.trim() && !blockReason && !send.isPending) send.mutate();
        }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Toggle value={channel} onChange={(v) => { setChannel(v); setTemplateId(""); }} options={[{ id: "sms", label: t("inbox.channel.sms"), icon: <MessageSquare className="h-4 w-4" /> }, { id: "email", label: t("inbox.channel.email"), icon: <Mail className="h-4 w-4" /> }]} />
          <Toggle
            value={category}
            onChange={(v) => { setCategory(v); setTemplateId(""); }}
            options={[
              { id: "transactional", label: t("inbox.category.transactional") },
              { id: "marketing", label: t("inbox.category.marketing"), disabled: channel === "sms" ? !th.consent.smsMarketing : !th.consent.emailMarketing, title: t("inbox.marketingBlocked") },
            ]}
          />
          {channel === "sms" && m?.sms ? <Badge tone={smsLive ? "green" : "neutral"}>{t(`msg.state.${m.sms.state}`)}</Badge> : null}
          {channel === "email" && m ? <Badge tone={m.emailMode === "live" ? "green" : "neutral"}>{m.emailMode === "live" ? t("msg.state.live") : t("msg.state.demo")}</Badge> : null}
          <div className="flex-1" />
          <Button type="button" variant="outline" size="sm" onClick={() => setCallOpen((v) => !v)}><Phone className="h-4 w-4" />{t("inbox.logCall")}</Button>
          {canSimulate ? <Button type="button" variant="outline" size="sm" onClick={() => setSimOpen((v) => !v)} aria-pressed={simOpen}><FlaskConical className="h-4 w-4" />{t("msg.simulateReply")}</Button> : null}
        </div>
        {callOpen ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end rounded-2xl bg-surface p-3">
            <Input className="flex-1" label={t("inbox.callSummary")} value={callSummary} onChange={(e) => setCallSummary(e.target.value)} />
            <Button type="button" onClick={() => callSummary.trim() && logCall.mutate()} loading={logCall.isPending}>{t("inbox.logCall")}</Button>
          </div>
        ) : null}
        {simOpen && canSimulate ? (
          <div className="rounded-2xl border border-dashed border-amber-400 bg-amber-50 p-3 space-y-2" data-testid="simulate-reply">
            <p className="text-xs font-semibold text-amber-900 inline-flex items-center gap-1"><FlaskConical className="h-3.5 w-3.5" />{t("msg.simulateReply")} · {t("msg.simulated")}</p>
            <p className="text-xs text-amber-900/80">{t("msg.simulateReply.help")}</p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <Input className="flex-1" placeholder={t("msg.simulateReply.placeholder")} value={simBody} onChange={(e) => setSimBody(e.target.value)} maxLength={1000} />
              <Button type="button" variant="secondary" onClick={() => simBody.trim() && simulate.mutate()} loading={simulate.isPending} disabled={!simBody.trim()}>{t("msg.simulateReply.send")}</Button>
            </div>
          </div>
        ) : null}
        <div className="grid gap-2 sm:grid-cols-2">
          <Select label={t("inbox.template")} value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
            <option value="">{t("inbox.noTemplate")}</option>
            {channelTemplates.map((tp) => (
              <option key={tp.id} value={tp.id}>{tp.name}</option>
            ))}
          </Select>
          {channel === "email" ? <Input label={t("inbox.subject")} value={subject} onChange={(e) => setSubject(e.target.value)} /> : <div className="hidden sm:block" />}
        </div>
        <Textarea placeholder={t("inbox.placeholder")} value={body} onChange={(e) => setBody(e.target.value)} className="min-h-[72px]" maxLength={4000} />
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-ink/55 min-h-[1rem]">{blockReason ?? (channel === "sms" && m?.sms?.blocker ? m.sms.blocker : channel === "email" && m?.emailMode === "fixture" ? t("inbox.modeNote") : channel === "sms" && smsLive ? t("msg.liveNote") : "")}</p>
          <Button type="submit" disabled={!body.trim() || !!blockReason} loading={send.isPending} title={blockReason ?? undefined}>
            <Send className="h-4 w-4" />{t("inbox.send")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Toggle<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: Array<{ id: T; label: string; icon?: React.ReactNode; disabled?: boolean; title?: string }> }) {
  return (
    <div className="inline-flex rounded-xl border border-ink/15 overflow-hidden" role="group">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          disabled={o.disabled}
          title={o.disabled ? o.title : undefined}
          onClick={() => onChange(o.id)}
          aria-pressed={value === o.id}
          className={cn("inline-flex min-h-[40px] items-center gap-1 px-3 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed", value === o.id ? "bg-ink text-white" : "text-ink/70 hover:bg-ink/5")}
        >
          {o.icon}{o.label}
        </button>
      ))}
    </div>
  );
}

const DELIVERY_TONE: Record<string, "neutral" | "green" | "amber" | "red" | "blue"> = { delivered: "green", received: "green", failed: "red", undelivered: "red", not_sent: "red", queued: "neutral", accepted: "neutral", sending: "neutral", sent: "blue", simulated: "neutral" };

function Bubble({ msg, locale }: { msg: Message; locale: "en" | "es" }) {
  const t = useT();
  const out = msg.direction === "outbound";
  const blocked = msg.status.startsWith("blocked") || msg.status === "failed";
  const simulated = msg.executionMode === "fixture" && msg.channel !== "call";
  const Icon = msg.channel === "sms" ? MessageSquare : msg.channel === "email" ? Mail : msg.channel === "call" ? Phone : StickyNote;
  const delivery = msg.providerStatus && msg.providerStatus !== "simulated" && msg.channel === "sms" ? msg.providerStatus : null;
  const deliveryLabel = delivery ? (t(`msg.delivery.${delivery}`) === `msg.delivery.${delivery}` ? delivery : t(`msg.delivery.${delivery}`)) : null;
  return (
    <li className={cn("flex", out ? "justify-end" : "justify-start")}>
      <div className={cn("max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm", out ? (blocked ? "bg-amber-50 border border-amber-300 text-ink" : "bg-primary text-white") : "bg-white border border-ink/10", msg.channel === "call" && "bg-surface border border-ink/10 text-ink", simulated && !blocked && "outline outline-1 outline-dashed outline-amber-400/70")}>
        {msg.subject ? <p className="font-semibold">{msg.subject}</p> : null}
        <p className="whitespace-pre-wrap break-words">{msg.body}</p>
        <p className={cn("mt-1 flex flex-wrap items-center gap-1 text-[11px]", out && !blocked && msg.channel !== "call" ? "text-white/80" : "text-ink/55")}>
          <Icon className="h-3 w-3" />
          {t(`inbox.channel.${msg.channel}`)} · {shortDate(msg.createdAt, locale)} {new Date(msg.createdAt).toLocaleTimeString(locale === "es" ? "es-MX" : "en-US", { hour: "numeric", minute: "2-digit" })}
          <Badge tone={blocked ? "amber" : msg.status === "delivered" || msg.status === "received" ? "green" : "neutral"} className="normal-case">{t(`inbox.status.${msg.status}`)}</Badge>
          {deliveryLabel ? <Badge tone={DELIVERY_TONE[delivery!] ?? "neutral"} className="normal-case"><span title={msg.providerError ? `${t("msg.error")}: ${msg.providerError}` : `${t("msg.delivery")}: ${delivery}${msg.providerStatusAt ? ` · ${shortDate(msg.providerStatusAt, locale)}` : ""}`}>{deliveryLabel}{msg.providerError ? " ⚠" : ""}</span></Badge> : null}
          {msg.aiDrafted ? <Badge tone="blue"><Sparkles className="h-3 w-3" /> {t("inbox.aiDrafted")}</Badge> : null}
          {simulated ? <Badge tone="amber" className="normal-case"><span title={t("msg.simulated.help")}><FlaskConical className="h-3 w-3 inline" /> {t("msg.simulated")}</span></Badge> : null}
        </p>
      </div>
    </li>
  );
}
